import { GenericActivityListPagination, GenericActivityListSort } from 'src/models/genericActivityList';
import { IdoButton } from 'src/models/ido';
import { AbstractQuery } from 'src/models/query';

import type { IdoRelationFilterEntry } from '../types';

export class GetTheIdoActivityModuleBoxesForTheIdoQuery extends AbstractQuery {
	cacheable = false;
	ignoreCachedResult = true;
	name = 'IFS_DesktopModule_IDOActivity_Query_Queries_GetTheIdoActivityModuleBoxesForTheIdoQuery';
	page = 1;
	subscribeForUpdates = false;
}

export class GetTheIdoActivityModuleIdoActivitiesForTheIdoQuery extends AbstractQuery {
	limit = 100;
	name = 'IFS_DesktopModule_IDOActivity_Query_Queries_GetTheIdoActivityModuleIdoActivitiesForTheIdoQuery';
	page = 1;
	sort: Array<GenericActivityListSort> = [
		{
			direction: 'DESC',
			property: 'timestamp',
		},
	];

	private readonly aggregateId: string = '';

	constructor(
		idoButton: IdoButton,
		boxIdFilter: Array<string>,
		userIdFilter: Array<number>,
		dateFilter: Array<string> | string,
		view: 'all' | 'open',
		direction: 'combined' | 'in' | 'out',
		searchTerm: string,
		pagination: GenericActivityListPagination,
		sort: GenericActivityListSort
	) {
		super();

		this.aggregateId = idoButton.aggregateId;
		this.page = pagination.page;
		this.limit = pagination.rowsPerPage;
		this.sort = [sort];

		if (view === 'open') {
			this.parameters.push({
				DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButton:
					'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonUnfinished',
			});
		} else {
			this.parameters.push({
				DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButton:
					'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonAll',
			});
		}

		this.parameters.push({
			DesktopModuleToolbarButtonGlobalizationFilter: true,
		});
		this.parameters.push({ DesktopModuleToolbarButtonArchivFilter: true });

		this.parameters.push({
			DesktopModuleToolbarDatefieldDateFilter: dateFilter,
		});

		this.parameters.push({
			DesktopModuleToolbarTextfieldSearchField: searchTerm,
		});

		this.parameters.push({
			DesktopModuleToolbarHiddenfieldSearchField: '',
		});

		type ParsedAggregateId = {
			entryId: number;
			idoId: number;
			instanceId: number;
			moduleName: string;
			sourceContainerId: number;
			targetContainerId: number;
			userId: number;
		};

		type IdoLinkingId = {
			idoId: number;
			instanceId: number;
		};

		const idoLinkings: Array<IdoLinkingId> = [];

		if (idoButton !== undefined) {
			idoButton.idoWindow.idoWindowIdoInstances.forEach((idoWindowInstance) => {
				idoWindowInstance.idoWindowIdos.forEach((idoWindowIdo) => {
					const parsedAggregateId: ParsedAggregateId = JSON.parse(
						idoWindowIdo.aggregateId
					) as ParsedAggregateId;
					idoLinkings.push({
						idoId: parsedAggregateId.idoId,
						instanceId: parsedAggregateId.instanceId,
					});
				});
			});

			this.parameters.push({
				DesktopModuleCustomFilterCondition: {
					idoLinkings: idoLinkings,
				},
			});
		}

		switch (direction) {
			case 'in':
				this.parameters.push({
					DesktopModuleToolbarCycleButtonDirection: 'DesktopModuleToolbarCycleButtonDirectionIncoming',
				});
				break;

			case 'out':
				this.parameters.push({
					DesktopModuleToolbarCycleButtonDirection: 'DesktopModuleToolbarCycleButtonDirectionOutgoing',
				});
				break;

			case 'combined':
				this.parameters.push({
					DesktopModuleToolbarCycleButtonDirection: 'DesktopModuleToolbarCycleButtonDirectionBidirectional',
				});
				break;
		}

		this.parameters.push({ DesktopModuleBoxIds: boxIdFilter });

		this.parameters.push({
			DesktopModuleToolbarSplitButtonUserFilter: userIdFilter,
		});

		this.disableSubscriptionsWhileSearchIsActive(searchTerm);
	}

	uniqueQueryIdentifier(): string {
		return this.createStableIdentifier({
			aggregateId: this.aggregateId,
		});
	}
}

export class GetTheIdoActivityModuleIdoActivitiesForTheIdoByIdoAndInstanceIdQuery extends AbstractQuery {
	limit = 100;
	name = 'IFS_DesktopModule_IDOActivity_Query_Queries_GetTheIdoActivityModuleIdoActivitiesForTheIdoQuery';
	page = 1;
	sort: Array<GenericActivityListSort> = [
		{
			direction: 'DESC',
			property: 'timestamp',
		},
	];

	private readonly aggregateId: string = '';

	constructor(
		idoId: number,
		idoInstanceId: number,
		boxIdFilter: Array<string>,
		userIdFilter: Array<number>,
		dateFilter: Array<string> | string,
		view: 'all' | 'open',
		direction: 'combined' | 'in' | 'out',
		searchTerm: string,
		pagination: GenericActivityListPagination,
		sort: GenericActivityListSort,
		idoRelations: Array<IdoRelationFilterEntry> = []
	) {
		super();

		this.aggregateId = `idoId:${idoId}-idoInstanceId:${idoInstanceId}`;
		this.page = pagination.page;
		this.limit = pagination.rowsPerPage;
		this.sort = [sort];

		if (view === 'open') {
			this.parameters.push({
				DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButton:
					'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonUnfinished',
			});
		} else {
			this.parameters.push({
				DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButton:
					'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonAll',
			});
		}

		this.parameters.push({
			DesktopModuleToolbarButtonGlobalizationFilter: true,
		});
		this.parameters.push({ DesktopModuleToolbarButtonArchivFilter: true });

		this.parameters.push({
			DesktopModuleToolbarDatefieldDateFilter: dateFilter,
		});

		this.parameters.push({
			DesktopModuleToolbarTextfieldSearchField: searchTerm,
		});

		this.parameters.push({
			DesktopModuleToolbarHiddenfieldSearchField: '',
		});

		type IdoLinkingId = {
			idoId: number;
			instanceId: number;
		};

		const idoLinkings: Array<IdoLinkingId> = [];

		idoLinkings.push({
			idoId: idoId,
			instanceId: idoInstanceId,
		});

		const customFilterCondition: {
			idoLinkings: Array<IdoLinkingId>;
			idoRelations?: Array<IdoRelationFilterEntry>;
		} = {
			idoLinkings: idoLinkings,
		};

		const [relationsHistorySource] = idoRelations;
		if (relationsHistorySource !== undefined) {
			customFilterCondition.idoRelations = idoRelations;

			this.aggregateId = `${this.aggregateId}-relations:${relationsHistorySource.idoId}:${relationsHistorySource.instanceId}`;
		}

		this.parameters.push({
			DesktopModuleCustomFilterCondition: customFilterCondition,
		});

		switch (direction) {
			case 'in':
				this.parameters.push({
					DesktopModuleToolbarCycleButtonDirection: 'DesktopModuleToolbarCycleButtonDirectionIncoming',
				});
				break;

			case 'out':
				this.parameters.push({
					DesktopModuleToolbarCycleButtonDirection: 'DesktopModuleToolbarCycleButtonDirectionOutgoing',
				});
				break;

			case 'combined':
				this.parameters.push({
					DesktopModuleToolbarCycleButtonDirection: 'DesktopModuleToolbarCycleButtonDirectionBidirectional',
				});
				break;
		}

		this.parameters.push({ DesktopModuleBoxIds: boxIdFilter });

		this.parameters.push({
			DesktopModuleToolbarSplitButtonUserFilter: userIdFilter,
		});

		this.disableSubscriptionsWhileSearchIsActive(searchTerm);
	}

	uniqueQueryIdentifier(): string {
		return this.createStableIdentifier({
			aggregateId: this.aggregateId,
		});
	}
}
